import React, { useState, useEffect, useMemo } from "react";
import {
  ChartExtraBar,
  ChartExtraListBar,
  HeroRanking,
  initSzieForScale,
  TextBox,
  Title,
  formartChartText,
  validateData,
} from "./common";
import { ChartsType, RankingData, KindDataType, horizontalBarType, ICharts, ISoftData } from "@Types/kindSumReport";
import { host } from "@Services/url";
import { ObserverWrapper } from "@Components/article/superTest/observerLayout";
import BarGroup from "./chart/barGroup";
import HorizontalBarGroup from "./chart/horizontalBarGroup";
import HorizontalBar from "./chart/horizontalBar";
import Speedways from "./speedways";
import { useQuery } from "react-query";
import { fetchSuperTestCharts, fetchSuperTestRank } from "@Services/kindsum";

interface IProps {
  spaceModelYearId: number;
  performanceModelYearId: number;
  // data: ISoftData;
  // rankData: RankingData[];
}

interface ISpeedChartsProps {
  res: ICharts | false;
  kindData: KindDataType;
  [key: string]: any;
}

// 加速实测
const CarSpeedUpChartDom: React.FC<ISpeedChartsProps> = ({ res, kindData }) => {
  const [upKeys, setUpKeys] = useState<string[]>([]);

  if (res) {
    const { kindInfo } = kindData;
    const titles = [["product"].concat(res.chartData.map((item) => item.title))];
    const dataSource = (res.chartData as any[]).map((item) => [item.title, ...item.value.map((item) => Number(item))]);
    const extraKeys = (res.chartData as horizontalBarType[])[0]?.key;

    const extraDataArr = extraKeys
      .map((item, inx) => {
        const data = kindInfo.filter((i) => i.myid === item)?.[0];

        return data
          ? { name: data.title, value: data.environment, color: "#3264FF" }
          : { name: "", value: "", color: "#3264FF" };
      })
      .filter((item) => item.name !== "");
    const data = titles.concat(dataSource as any);

    return (
      <div className={"supertest-observer"}>
        <ObserverWrapper
          name='0to100Accelerate'
          observerOnceCallBack={(key) => setUpKeys((prev) => (!prev.includes(key) ? prev.concat(key) : prev))}
        >
          <BarGroup
            id='0to100Accelerate'
            width={initSzieForScale(270)}
            height={initSzieForScale(170)}
            dataSource={data}
            visibleKeys={upKeys}
          />
        </ObserverWrapper>
        <ChartExtraBar dataSource={extraDataArr} />
      </div>
    );
  }
  return <></>;
};

// 耗能实测
const CarEnergyDom: React.FC<ISpeedChartsProps> = ({ res, kindData }) => {
  const data = kindData?.["energyConsumption"];
  const [upKeys, setUpKeys] = useState<string[]>([]);

  if (res && data && data.length !== 0) {
    const { kindInfo } = kindData;
    const titles = [["product"].concat(kindInfo.map((item) => item.title))];
    const dataSource = (res.chartData as any[]).map((item) => [item.title, ...item.value.map((item) => Number(item))]);

    return (
      <div className={"supertest-observer mt-10"}>
        <ObserverWrapper
          name='cityFuelConsumption'
          observerOnceCallBack={(key) => setUpKeys((prev) => (!prev.includes(key) ? prev.concat(key) : []))}
        >
          <HorizontalBarGroup
            id='cityFuelConsumption'
            width={initSzieForScale(270)}
            height={initSzieForScale(170)}
            dataSource={titles.concat(dataSource as any)}
            visibleKeys={upKeys}
          />
        </ObserverWrapper>
        <ChartExtraListBar dataSource={data} />
      </div>
    );
  }

  return <></>;
};

// 煞停实测
const CarBrakeChartDom: React.FC<ISpeedChartsProps> = ({ res, kindData }) => {
  const [upKeys, setUpKeys] = useState<string[]>([]);

  if (res && kindData?.["100to0Brake"] && kindData?.["100to0Brake"]?.length !== 0) {
    const { kindInfo } = kindData;
    const titles = kindInfo.map((item) => item.title);
    const dataSource = res.chartData?.[0]?.value ?? [];
    const data = kindData["100to0Brake"].map((item) => ({ name: item.title, value: item.environment }));

    return (
      <div className={"supertest-observer mt-10"}>
        <ObserverWrapper
          name='100to0Brake'
          observerOnceCallBack={(key) => setUpKeys((prev) => (!prev.includes(key) ? prev.concat(key) : []))}
        >
          <HorizontalBar
            id='100to0Brake'
            width={initSzieForScale(270)}
            height={initSzieForScale(170)}
            titles={titles}
            dataSource={dataSource}
            visibleKeys={upKeys}
          />
        </ObserverWrapper>
        <ChartExtraBar dataSource={data} />
      </div>
    );
  }

  return <></>;
};

const defaultSoftData: ISoftData = {
  chartData: {},
  compareMyid: 0,
  kindData: {} as KindDataType,
  myids: 0,
  rankData: [],
};

// 赛道实测
const CarSpeedWayDom: React.FC<ISpeedChartsProps> = ({ kindData, speedWayData }) => {
  const data = kindData?.["speedways"];

  if (data && data.length !== 0) {
    return (
      <div className={"speedway-observer"}>
        <ObserverWrapper name='speedway'>
          <Speedways id='speedway' dataSource={data} speedWayData={speedWayData} />
        </ObserverWrapper>
      </div>
    );
  }
  return <></>;
};

const Performance: React.FC<IProps> = ({ spaceModelYearId, performanceModelYearId }) => {
  const [chartMapData, setChartsMapData] = useState<ChartsType>({});
  const [kindData, setKindData] = useState<KindDataType>();

  const { data = defaultSoftData } = useQuery<ISoftData>(
    ["fetchPerformanceCharts", performanceModelYearId],
    async () => {
      if (!spaceModelYearId) return defaultSoftData;
      try {
        const res: { data; status } = await fetchSuperTestCharts({
          myid: performanceModelYearId,
          type: "performance",
        });
        if (res?.status === 12000) {
          return res.data;
        }
      } catch (error) {}
      return defaultSoftData;
    },
  );

  const { data: rankData = [] } = useQuery<Array<RankingData>>(
    ["fetchSuperTestRank", performanceModelYearId],
    async () => {
      if (!spaceModelYearId) return [];
      try {
        const res: { data; status } = await fetchSuperTestRank({ myid: performanceModelYearId });
        if (res?.status === 12000) {
          return res.data.data;
        }
      } catch (error) {}
      return [];
    },
  );

  const formateMapData = (data: ChartsType) => {
    const obj = {};

    for (let key in data) {
      const mapkey = data[key]?.["templateId"];

      if (mapkey) {
        obj[mapkey] = data[key];
      }
    }

    setChartsMapData(obj);
  };

  const SpeendData = useMemo(() => validateData(chartMapData, 11), [chartMapData]);
  const SpeendTitle = useMemo(() => {
    return validateData(chartMapData, 11)
      ? validateData(chartMapData, 11)!.chartData.map((item: any) => item.title)
      : [];
  }, [chartMapData]);
  const EnergyData = useMemo(() => validateData(chartMapData, 13), [chartMapData]);
  const BrakeChartData = useMemo(() => validateData(chartMapData, 12), [chartMapData]);
  const RankingData = useMemo(() => {
    return Array.isArray(rankData) && rankData.length > 0
      ? rankData.reduce((prev, next) => ({ ...prev, [next.key]: next }), {})
      : {};
  }, [rankData]);
  const EnergyRankData = useMemo(() => {
    if (Array.isArray(rankData) && rankData.length > 0) {
      const data = rankData.filter((item) => item.key === "fuel_consumption").sort((a, b) => a.rank - b.rank);

      return Array.isArray(data) && data.length > 0 ? { [data[0].key]: data[0] } : {};
    }
    return {};
  }, [rankData]);
  const SpeedWayData = useMemo(() => {
    if (Array.isArray(rankData) && rankData.length > 0) {
      const data = rankData.filter((item) => item.key === "speedway");

      return Array.isArray(data) && data.length > 0 ? data[0] : {};
    }
    return {};
  }, [rankData]);

  useEffect(() => {
    setKindData(data.kindData);
    formateMapData(data.chartData);
  }, [data]);

  return (
    <div className='Performance pt-30'>
      {SpeendData && (
        <>
          <Title title='加速實測' />
          <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-50 pt-10'>
            <HeroRanking
              data={RankingData}
              rankKey={"accelerate"}
              href={`${host.c}/kind/testing/ranking?fid=accelerate&tid=0`}
            />
            {!!formartChartText(SpeendData.chartData).length &&
              formartChartText(SpeendData.chartData).map((item, inx) => (
                <div className='flex items-center justify-center my-10' key={inx}>
                  {item.map((ii: string, index: number) => (
                    <div className='flex flex-col items-center flex-auto' key={index}>
                      <span className='text-[#3264FF] text-16 font-medium mb-4'>{ii}秒</span>
                      <p className='text-[#999999] text-12'>{SpeendTitle[index]}</p>
                    </div>
                  ))}
                </div>
              ))}
            {SpeendData.chartName && <TextBox text={SpeendData.chartName} />}
            {kindData && <CarSpeedUpChartDom res={SpeendData} kindData={kindData} />}
          </div>
        </>
      )}

      {EnergyData && (
        <>
          <Title title='耗能實測' />
          <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-50 pt-10'>
            <HeroRanking
              data={EnergyRankData}
              rankKey={"fuel_consumption"}
              href={`${host.c}/kind/testing/ranking?fid=fuel_consumption_high_speed&tid=0`}
            />
            {kindData && <CarEnergyDom res={EnergyData} kindData={kindData} />}
          </div>
        </>
      )}

      {BrakeChartData && (
        <>
          <Title title='煞停實測' />
          <div className='border-2 border-solid border-[#EAEFFF] rounded-4 mx-4 pb-4 mb-50 pt-10'>
            <HeroRanking data={RankingData} rankKey={"brake"} href={`${host.c}/kind/testing/ranking?fid=brake&tid=0`} />
            {kindData && <CarBrakeChartDom res={BrakeChartData} kindData={kindData} />}
          </div>
        </>
      )}

      {Array.isArray(kindData?.["speedways"]) && kindData?.["speedways"]?.length > 0 && (
        <>
          <Title title='賽道實測' />
          <div className='mx-4'>
            {kindData && <CarSpeedWayDom res={BrakeChartData} kindData={kindData} speedWayData={SpeedWayData} />}
          </div>
        </>
      )}
    </div>
  );
};

export default Performance;
